import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import FileDropZone from '../components/FileDropZone';
import { getKstDateString } from '../utils/dateKst';

const CAT_COLOR = { '긴급': '#dc2626', '중요': '#d97706', '일반': '#185FA5' };
const CAT_BG    = { '긴급': '#fee2e2', '중요': '#fef3c7', '일반': '#E6F1FB' };

// ── 공개범위 7단계 ──────────────────────────────────────────
// 임원이상 < 부서장이상 < 팀원이상(관리직 전체) < 생산관리포함 < 사내전체 < (협력사는 별도 축) < 모두공개
// - 관리직 전체 : 사내 임원·부서장·팀원까지만 (생산 및 품질 제외)
// - 생산관리포함 : 관리직 전체 + 생산부서장·품질부서장까지
// - 사내전체 : 생산관리포함 + 생산직원·품질직원 포함 (사내 전 직원)
// - 협력사 : 협력사 계정에게만 공개
// - 모두공개 : 사내전체 + 협력사 모두에게 공개
// 각 계정의 등급은 계정관리 > 조직구성에서 지정한 부서 유형(관리직/생산/품질) + 직급 등급(임원/부서장/팀원)으로 자동 계산됩니다.
const VIS_LIST = [
  { val:'임원이상',     label:'임원이상',             desc:'임원까지만 공개',                          color:'#993556', bg:'#FBEAF0' },
  { val:'부서장이상',   label:'부서장이상',           desc:'임원, 부서장까지 공개',                      color:'#185FA5', bg:'#E6F1FB' },
  { val:'팀원이상',     label:'팀원이상(관리직 전체)', desc:'관리직 임원·부서장·팀원까지 (생산/품질 제외)', color:'#0F6E56', bg:'#E1F5EE' },
  { val:'생산관리포함', label:'생산관리 포함',        desc:'관리직 전체 + 생산·품질 부서장까지',          color:'#7C3AED', bg:'#EDE9FE' },
  { val:'사내전체',     label:'사내전체',             desc:'생산관리 포함 + 생산·품질 직원까지 (사내 전원)', color:'#854F0B', bg:'#FAEEDA' },
  { val:'협력사',       label:'협력사',               desc:'협력사 계정에게만 공개',                      color:'#0369a1', bg:'#e0f2fe' },
  { val:'모두공개',     label:'모두공개',             desc:'사내전체 + 협력사 모두에게 공개',              color:'#16a34a', bg:'#dcfce7' },
];
const visLabel = (val) => (VIS_LIST.find(v => v.val === val)?.label || val);

// ── 공개범위 설정(계정관리)에서 만든 공개구분을 선택 목록에 표시할 때 쓰는 형식 ──
// 공개구분명만 표시합니다 (회원구분/부서구분/직급구분 등 괄호 안 상세 내용은 목록에서 제외)
function formatScopeLabel(scope) {
  return scope.name;
}

export default function Notices() {
  const [list, setList]         = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [search, setSearch]     = useState('');
  const [msg, setMsg]           = useState('');

  const user    = JSON.parse(sessionStorage.getItem('user') || '{}');
  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 값을 그대로 따릅니다. 삭제는 계정관리 상세설정의
  // "삭제가능 여부설정"(delete_allowed)으로 개별 부여합니다(예전에는 관리자만 무조건 가능했음).
  const { canWrite: canWriteMenu, hasDetail } = usePermissions();
  const canWriteNotices  = canWriteMenu('notices');
  const canDeleteNotice  = hasDetail('notices', 'delete_allowed');

  const load = () => API.get('/notices').then(r => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleSelect = async (notice) => {
    const res = await API.get('/notices/' + notice.id);
    setSelected(res.data);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete('/notices/' + id);
    setSelected(null);
    setMsg('삭제 완료!');
    load();
  };

  // 화면 하단 "최신글/업데이트" 활동피드 바에서 공지사항 항목을 클릭해 들어온 경우, 그 항목의
  // 상세 모달을 바로 연다. 한 번 열면 state를 지워서 새로고침/뒤로가기 시 다시 열리지 않게 한다.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const ao = location.state?.activityOpen;
    if (ao) {
      handleSelect({ id: ao.itemId });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]); // eslint-disable-line

  const pinned = list.filter(n => n.is_pinned === 1);
  const normal = list
    .filter(n => n.is_pinned === 0)
    .filter(n => n.title.includes(search) || (n.content || '').includes(search));

  // 목록 인쇄 — 화면 캡처가 아닌 별도 창에 실제 표(<table>)를 작성해서 인쇄함.
  // (기존 방식은 화면 전체를 CSS로 숨기고 찍는 방식이라, 사이드바 등 화면에 보이지 않는 나머지
  //  요소들의 레이아웃 높이가 남아 있어 실제 내용이 한 페이지 분량인데도 뒤에 빈 페이지가
  //  하나 더 인쇄되는 문제가 있었음 — 업무관리 목록 인쇄와 동일한 방식으로 교체)
  const handleListPrint = () => {
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(user); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)
    const esc = (s) => String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const rowsHtml = [...pinned, ...normal].map(n => `<tr>
        <td>${esc(n.category)}</td>
        <td style="text-align:left;">${n.is_pinned===1?'📌 ':''}${esc(n.title)}</td>
        <td>${esc(n.created_by)}</td>
        <td>${n.view_count||0}</td>
        <td>${n.created_at ? esc(n.created_at.slice(0,10)) : ''}</td>
        <td>${esc(visLabel(n.visibility))}</td>
      </tr>`).join('');

    const pw = window.open('', '_blank', 'width=1200,height=800');
    pw.document.write(`<!DOCTYPE html><html><head><title>공지사항</title>
      <style>
        @page { size: A4 landscape; margin: 0; }
        body { font-family: sans-serif; color:#222; padding:10mm; box-sizing:border-box; }
        h2 { font-size:18px; margin:0 0 10px; }
        table { width:100%; border-collapse:collapse; font-size:11px; }
        th, td { border:1px solid #ccc; padding:5px 6px; text-align:center; }
        th { background:#f4f4f4; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        .footer { display:flex; justify-content:space-between; margin-top:10px; padding-top:6px;
          border-top:1px solid #ddd; font-size:10px; color:#666; }
      </style></head><body>
      <h2>📢 공지사항</h2>
      <table>
        <thead><tr>
          <th>구분</th><th>제목</th><th>작성자</th><th>조회</th><th>작성일</th><th>공개범위</th>
        </tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="6" style="color:#999;">표시할 공지사항이 없습니다.</td></tr>`}</tbody>
      </table>
      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${printedBy}</span></div>
      </body></html>`);
    pw.document.close(); pw.focus();
    logPrint('공지사항', '공지사항 목록');
    setTimeout(() => { pw.print(); pw.close(); }, 400);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1690 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>📢 공지사항</h2>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>전체 공지사항을 확인할 수 있습니다.</p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={handleListPrint}
            style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🖨️ 인쇄
          </button>
          {canWriteNotices && (
            <button
              onClick={() => { setShowForm(true); setEditData(null); }}
              style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              + 공지 등록
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 16px',
          borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{msg}</div>
      )}

      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="🔍 제목, 내용 검색..."
        style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 8,
          fontSize: 14, marginBottom: 16, boxSizing: 'border-box' }}
      />

      {/* 공지 목록: 헤더(구분/제목/작성자/조회/작성일/공개범위) 아래에 고정 공지 → 일반 공지 순서로 표시 */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 80px 70px 100px 180px',
          background: '#f8fafc', padding: '10px 16px', fontSize: 14, fontWeight: 600,
          color: '#666', borderBottom: '1px solid #e5e7eb' }}>
          <span>구분</span>
          <span>제목</span>
          <span>작성자</span>
          <span style={{ textAlign: 'center' }}>조회</span>
          <span style={{ textAlign: 'center' }}>작성일</span>
          <span style={{ textAlign: 'center' }}>공개범위</span>
        </div>

        {pinned.length > 0 && (
          <>
            {pinned.map((n, i) => (
              <NoticeRow
                key={n.id} notice={n}
                onClick={() => handleSelect(n)}
                isLast={false}
                pinned={true}
              />
            ))}
            <div style={{ borderTop: '2px solid #e5e7eb' }} />
          </>
        )}

        {normal.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 0', color: '#aaa', fontSize: 14 }}>
            공지사항이 없습니다.
            {canWriteNotices && <div style={{ fontSize: 13, marginTop: 6 }}>상단 "+ 공지 등록" 버튼을 눌러 추가하세요.</div>}
          </div>
        ) : (
          normal.map((n, i) => (
            <NoticeRow
              key={n.id} notice={n}
              onClick={() => handleSelect(n)}
              isLast={i === normal.length - 1}
              pinned={false}
            />
          ))
        )}
      </div>

      {/* 상세 모달 */}
      {selected && (
        <NoticeDetail
          notice={selected}
          canDeleteNotice={canDeleteNotice}
          canWriteNotices={canWriteNotices}
          onClose={() => setSelected(null)}
          onEdit={(n) => { setEditData(n); setShowForm(true); setSelected(null); }}
          onDelete={handleDelete}
        />
      )}

      {/* 등록/수정 폼 */}
      {showForm && (
        <NoticeForm
          editData={editData}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={() => {
            setShowForm(false);
            setEditData(null);
            load();
            setMsg(editData ? '수정 완료!' : '등록 완료!');
          }}
        />
      )}
    </div>
  );
}

// ── 공지 행 컴포넌트 ──────────────────────────────
function NoticeRow({ notice, onClick, isLast, pinned }) {
  const n = notice;
  return (
    <div
      onClick={onClick}
      style={{ display: 'grid', gridTemplateColumns: '70px 1fr 80px 70px 100px 180px',
        padding: '13px 16px', fontSize: 14, cursor: 'pointer', alignItems: 'center',
        borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
        background: pinned ? '#FFFBEB' : '#fff' }}
      onMouseEnter={e => { e.currentTarget.style.background = pinned ? '#FEF3C7' : '#f8fafc'; }}
      onMouseLeave={e => { e.currentTarget.style.background = pinned ? '#FFFBEB' : '#fff'; }}>
      <span>
        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 500,
          background: CAT_BG[n.category] || '#f0f0f0',
          color:      CAT_COLOR[n.category] || '#555' }}>
          {n.category}
        </span>
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {pinned && <span style={{ fontSize: 12 }}>📌</span>}
        <span style={{ fontWeight: pinned ? 600 : 400 }}>{n.title}</span>
        {n.file_name && <span style={{ fontSize: 11, color: '#185FA5' }}>📎</span>}
        {n.linked_schedule_date && (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10,
            background: '#E6F1FB', color: '#185FA5', fontWeight: 600 }}>
            📅 일정등록
          </span>
        )}
        {n.show_display === 1 && (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10,
            background: '#E1F5EE', color: '#0F6E56', fontWeight: 600 }}>
            📺 전광판
          </span>
        )}
      </span>
      <span style={{ color: '#888', fontSize: 14 }}>{n.created_by}</span>
      <span style={{ textAlign: 'center', color: '#aaa', fontSize: 14 }}>{n.view_count || 0}</span>
      <span style={{ textAlign: 'center', color: '#aaa', fontSize: 14 }}>
        {n.created_at ? n.created_at.slice(0, 10) : ''}
      </span>
      <span style={{ textAlign: 'center', color: '#888', fontSize: 13 }}>{visLabel(n.visibility)}</span>
    </div>
  );
}

// ── 상세 모달 (댓글 포함) ─────────────────────────────
function NoticeDetail({ notice, canDeleteNotice, canWriteNotices, onClose, onEdit, onDelete }) {
  const n = notice;
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const viewContentRef = useRef(null);

  const [comments, setComments]         = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [commentText, setCommentText]   = useState('');
  const [commentVis, setCommentVis]     = useState('사내전체');
  const [posting, setPosting]           = useState(false);
  const { hasDetail } = usePermissions();
  const canComment = hasDetail('notices', 'comment_allowed');

  // 본문에 붙여넣어진 스크린샷 이미지는 인증이 필요하므로, 렌더링 후 다시 인증된 URL로 채워넣음
  useEffect(() => {
    if (!viewContentRef.current) return;
    const imgs = Array.from(viewContentRef.current.querySelectorAll('img[data-filename]'));
    imgs.forEach(async (img) => {
      const filename = img.getAttribute('data-filename');
      if (!filename) return;
      try {
        const res = await API.get('/notices/image/' + filename, { responseType: 'blob' });
        img.src = window.URL.createObjectURL(res.data);
      } catch (e) { /* 이미지를 불러오지 못해도 나머지 내용은 그대로 둠 */ }
    });
  }, [n.content]);

  const loadComments = () => {
    setLoadingComments(true);
    API.get('/notices/' + n.id + '/comments')
      .then(r => setComments(r.data))
      .catch(() => setComments([]))
      .finally(() => setLoadingComments(false));
  };
  useEffect(() => { loadComments(); /* eslint-disable-next-line */ }, [n.id]);

  // 이 공지 하나만 별도 창에 인쇄 (업무관리 상세 인쇄와 동일한 방식: 실제 내용만큼만 인쇄됨)
  const handlePrint = () => {
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(user); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)

    const contentHtml = n.content
      ? `<div style="padding:12px;background:#f8fafc;border-radius:8px;font-size:13px;line-height:1.8;margin-bottom:16px;">${n.content}</div>`
      : `<div style="color:#999;margin-bottom:16px;">내용이 없습니다.</div>`;
    const fileHtml = n.file_name
      ? `<div style="margin-bottom:16px;font-size:13px;">📎 첨부파일: ${n.file_ori || n.file_name}</div>`
      : '';

    const pw = window.open('', '_blank', 'width=800,height=1000');
    pw.document.write(`<!DOCTYPE html><html><head><title>공지사항 - ${n.title}</title>
      <style>
        @page { size: A4 portrait; margin: 0; }
        body { font-family: sans-serif; color:#222; padding: 15mm 15mm 20mm; box-sizing:border-box; }
        h2 { font-size:20px; margin:0 0 6px; }
        .meta { font-size:12px; color:#888; margin-bottom:16px; }
        .footer { display:flex; justify-content:space-between; margin-top:14px; padding-top:8px;
          border-top:1px solid #ddd; font-size:11px; color:#555; }
      </style></head><body>
      <h2>📢 ${n.title}</h2>
      <div class="meta">구분: ${n.category} · 작성자: ${n.created_by || '-'} · 작성일: ${n.created_at ? n.created_at.slice(0,10) : ''} · 공개범위: ${visLabel(n.visibility)}</div>
      ${contentHtml}
      ${fileHtml}
      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${printedBy}</span></div>
      </body></html>`);
    pw.document.close(); pw.focus();
    logPrint('공지사항', n.title || '공지사항 상세');
    setTimeout(() => { pw.print(); pw.close(); }, 400);
  };

  const handleDownload = async () => {
    try {
      const res = await API.get('/notices/download/' + n.file_name, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = n.file_ori || n.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      logDownload('공지사항', n.file_ori || n.file_name);
    } catch (err) {
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setPosting(true);
    try {
      await API.post('/notices/' + n.id + '/comments', { content: commentText, visibility: commentVis });
      setCommentText('');
      loadComments();
    } catch (err) {
      alert(err.response?.data?.message || '댓글 등록 중 오류가 발생했습니다.');
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await API.delete('/notices/' + n.id + '/comments/' + cid);
      loadComments();
    } catch (err) {
      alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}>
      <div
        style={{ background: '#fff', borderRadius: 16, width: 580, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding: '22px 24px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, marginRight: 12 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 500,
                  background: CAT_BG[n.category] || '#f0f0f0',
                  color:      CAT_COLOR[n.category] || '#555' }}>{n.category}</span>
                {n.is_pinned === 1 && (
                  <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20,
                    background: '#fef3c7', color: '#d97706', fontWeight: 500 }}>📌 고정</span>
                )}
                {n.show_display === 1 && (
                  <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20,
                    background: '#E1F5EE', color: '#0F6E56', fontWeight: 500 }}>📺 전광판 노출</span>
                )}
                <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20,
                  background: '#f0f0f0', color: '#666', fontWeight: 500 }}>{visLabel(n.visibility)}</span>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>{n.title}</h3>
              <div style={{ fontSize: 12, color: '#aaa', display: 'flex', gap: 14 }}>
                <span>작성자: {n.created_by}</span>
                <span>작성일: {n.created_at ? n.created_at.slice(0, 10) : ''}</span>
                <span>조회: {n.view_count || 0}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button onClick={handlePrint}
                style={{ background: '#475569', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                🖨️ 인쇄하기
              </button>
              <button onClick={onClose}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>✕</button>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 24px', minHeight: 120 }}>
          {n.content ? (
            <div ref={viewContentRef}
              style={{ fontSize: 14, color: '#333', lineHeight: 1.8 }}
              dangerouslySetInnerHTML={{ __html: n.content }} />
          ) : (
            <div style={{ color: '#aaa', fontSize: 14 }}>내용이 없습니다.</div>
          )}
        </div>

        {n.file_name && (
          <div style={{ margin: '0 24px 20px', padding: '12px 16px', background: '#f0f6ff',
            borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#185FA5' }}>📎 {n.file_ori}</span>
            <button onClick={handleDownload}
              style={{ background: '#185FA5', color: '#fff', padding: '6px 14px', border: 'none',
                borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
              다운로드
            </button>
          </div>
        )}

        {(canWriteNotices || canDeleteNotice) && (
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 8 }}>
            {canWriteNotices && (
              <button onClick={() => onEdit(n)}
                style={{ flex: 1, padding: '10px', background: '#f0f6ff', color: '#185FA5',
                  border: '1px solid #185FA5', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                ✏️ 수정
              </button>
            )}
            {canDeleteNotice && (
              <button onClick={() => onDelete(n.id)}
                style={{ flex: 1, padding: '10px', background: '#fee2e2', color: '#dc2626',
                  border: '1px solid #dc2626', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                🗑 삭제
              </button>
            )}
          </div>
        )}

        {/* ── 댓글 ── */}
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '18px 24px 22px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 10 }}>
            💬 댓글{comments.length > 0 ? ` (${comments.length})` : ''}
          </div>

          {loadingComments ? (
            <div style={{ fontSize: 13, color: '#aaa', marginBottom: 12 }}>불러오는 중...</div>
          ) : comments.length === 0 ? (
            <div style={{ fontSize: 13, color: '#aaa', marginBottom: 12 }}>아직 댓글이 없습니다.</div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              {comments.map(c => (
                <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap', flex: 1 }}>{c.content}</div>
                    {(user.role === 'superadmin' || user.role === 'admin' || c.created_by === user.name) && (
                      <button onClick={() => deleteComment(c.id)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
                        삭제
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{c.created_by}</span>
                    <span>{c.created_at ? c.created_at.slice(0, 16).replace('T', ' ') : ''}</span>
                    <span style={{ padding: '1px 6px', borderRadius: 8, background: '#f0f0f0', color: '#888' }}>
                      {visLabel(c.visibility)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {canComment ? (
            <>
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="댓글을 입력하세요..."
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8,
                  fontSize: 13, boxSizing: 'border-box', height: 60, resize: 'vertical', marginBottom: 8, outline: 'none' }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={commentVis} onChange={e => setCommentVis(e.target.value)}
                  style={{ fontSize: 12, padding: '7px 8px', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>
                  {VIS_LIST.map(v => <option key={v.val} value={v.val}>{v.label}</option>)}
                </select>
                <button onClick={submitComment} disabled={posting || !commentText.trim()}
                  style={{ padding: '8px 16px', background: commentText.trim() ? '#185FA5' : '#ccc', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                    cursor: commentText.trim() ? 'pointer' : 'not-allowed' }}>
                  등록
                </button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: '#999' }}>댓글 작성 권한이 없습니다.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 등록/수정 폼 ──────────────────────────────────
function NoticeForm({ editData, onClose, onSaved }) {
  const { hasDetail } = usePermissions();
  const canExposeDisplay = hasDetail('notices', 'display_expose');
  const [form, setForm] = useState({
    category:        editData ? editData.category   : '일반',
    title:            editData ? editData.title      : '',
    content:          editData ? editData.content    : '',
    is_pinned:        editData && editData.is_pinned    === 1 ? '1' : '0',
    show_display:     editData && editData.show_display === 1 ? '1' : '0',
    visibility:       editData ? (editData.visibility || '') : '',
    add_to_schedule:  editData && editData.linked_schedule_date ? '1' : '0',
    schedule_date:    editData && editData.linked_schedule_date
      ? editData.linked_schedule_date.slice(0, 10)
      : getKstDateString(),
  });
  const [file, setFile]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [scopeOptions, setScopeOptions] = useState([]);

  // 계정관리 > 공개범위 설정에서 만든 공개구분 목록 (등록/수정 화면 공통)
  // 신규 등록(editData 없음)인 경우, 계정관리에서 지정한 기본값 공개구분을 자동으로 선택해둡니다.
  useEffect(() => {
    API.get('/disclosure/list').then(r => {
      const scopes = r.data.scopes || [];
      setScopeOptions(scopes);
      if (!editData) {
        const def = scopes.find(s => s.is_default);
        if (def) setForm(f => (f.visibility ? f : { ...f, visibility: def.name }));
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? (checked ? '1' : '0') : value });
  };

  // 내용 편집기 (이미지 붙여넣기 지원) — form.content 에는 HTML이 저장됨(이미지 태그 포함 가능)
  const contentRef = useRef(null);
  const contentLoadedRef = useRef(false);

  useEffect(() => {
    if (contentLoadedRef.current || !contentRef.current) return;
    contentLoadedRef.current = true;
    contentRef.current.innerHTML = form.content || '';
    hydrateContentImages();
    // eslint-disable-next-line
  }, []);

  const hydrateContentImages = async () => {
    if (!contentRef.current) return;
    const imgs = Array.from(contentRef.current.querySelectorAll('img[data-filename]'));
    for (const img of imgs) {
      const filename = img.getAttribute('data-filename');
      if (!filename) continue;
      try {
        const res = await API.get('/notices/image/' + filename, { responseType: 'blob' });
        img.src = window.URL.createObjectURL(res.data);
      } catch (e) { /* 이미지를 불러오지 못해도 나머지 내용은 그대로 둠 */ }
    }
  };

  const handleContentInput = () => {
    if (contentRef.current) setForm(f => ({ ...f, content: contentRef.current.innerHTML }));
  };

  // 클립보드에 이미지가 있으면 바로 삽입(로컬 미리보기) 후 서버에 업로드해서 나중에도 다시 불러올 수 있게 함
  const handleContentPaste = async (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) {
        imageFile = items[i].getAsFile();
        break;
      }
    }
    if (!imageFile) return; // 이미지가 아니면 기본 붙여넣기 동작 사용

    e.preventDefault();
    const localUrl = window.URL.createObjectURL(imageFile);
    document.execCommand('insertImage', false, localUrl);

    const imgs = contentRef.current ? contentRef.current.querySelectorAll(`img[src="${localUrl}"]`) : [];
    const insertedImg = imgs[imgs.length - 1];
    if (insertedImg) insertedImg.style.maxWidth = '100%';

    try {
      const fd = new FormData();
      fd.append('image', imageFile, imageFile.name || 'pasted.png');
      const res = await API.post('/notices/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (insertedImg && res.data.filename) insertedImg.setAttribute('data-filename', res.data.filename);
    } catch (err) {
      // 업로드에 실패해도 화면에는 미리보기가 남아있음(단, 나중에 다시 열면 안 보일 수 있음)
    }
    handleContentInput();
  };

  const handleSubmit = async () => {
    if (!form.title) { alert('제목을 입력해주세요.'); return; }
    if (!form.visibility) { alert('공개범위를 선택해주세요.'); return; }
    if (form.add_to_schedule === '1' && !form.schedule_date) { alert('일정 날짜를 선택해주세요.'); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append('category',        form.category);
    fd.append('title',           form.title);
    fd.append('content',         form.content);
    fd.append('is_pinned',       form.is_pinned);
    fd.append('show_display',    form.show_display);
    fd.append('visibility',      form.visibility);
    fd.append('add_to_schedule', form.add_to_schedule);
    fd.append('schedule_date',   form.add_to_schedule === '1' ? form.schedule_date : '');
    if (file) fd.append('file', file);
    try {
      if (editData) {
        await API.put('/notices/' + editData.id, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await API.post('/notices', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
    } catch (err) {
      alert(err.response ? err.response.data.message : '오류 발생');
    } finally {
      setLoading(false);
    }
  };

  const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8,
    fontSize: 14, boxSizing: 'border-box', marginBottom: 12, outline: 'none' };
  const lbl = { fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 };

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div
        style={{ background: '#fff', borderRadius: 16, padding: 28, width: 520,
          maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>{editData ? '공지 수정' : '공지 등록'}</h3>
            {canExposeDisplay && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: form.show_display==='1'?'#0F6E56':'#888' }}>
                  📺 생산전광판 노출
                </span>
                <input
                  type="checkbox"
                  name="show_display"
                  checked={form.show_display === '1'}
                  onChange={handleChange}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#0F6E56' }}
                />
              </label>
            )}
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>✕</button>
        </div>

        {/* 구분 + 상단고정 + 일정관리등록 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end', marginBottom: form.add_to_schedule === '1' ? 8 : 12 }}>
          <div>
            <label style={lbl}>구분</label>
            <select name="category" value={form.category} onChange={handleChange} style={{ ...inp, marginBottom: 0 }}>
              <option value="일반">일반</option>
              <option value="중요">중요</option>
              <option value="긴급">긴급</option>
            </select>
          </div>
          <div>
            <label style={lbl}>상단 고정</label>
            <select name="is_pinned" value={form.is_pinned} onChange={handleChange} style={{ ...inp, marginBottom: 0 }}>
              <option value="0">고정 안함</option>
              <option value="1">📌 상단 고정</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '9px 0', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: form.add_to_schedule==='1'?'#185FA5':'#555' }}>
              📅 일정관리 등록
            </span>
            <input
              type="checkbox"
              name="add_to_schedule"
              checked={form.add_to_schedule === '1'}
              onChange={handleChange}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#185FA5' }}
            />
          </label>
        </div>
        {form.add_to_schedule === '1' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ ...lbl, marginBottom: 4 }}>일정 날짜 *</label>
            <input type="date" name="schedule_date" value={form.schedule_date} onChange={handleChange}
              style={{ ...inp, marginBottom: 0, maxWidth: 200 }} />
          </div>
        )}

        {/* 제목 */}
        <label style={lbl}>제목 *</label>
        <input name="title" placeholder="공지 제목을 입력하세요"
          value={form.title} onChange={handleChange} style={inp} />

        {/* 내용 */}
        <label style={lbl}>
          내용 <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400 }}>(스크린샷을 복사해서 바로 붙여넣기(Ctrl+V) 할 수 있습니다)</span>
        </label>
        <div
          ref={contentRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleContentInput}
          onPaste={handleContentPaste}
          style={{ ...inp, height: 160, overflowY: 'auto', lineHeight: 1.6, cursor: 'text' }}
        />

        {/* 첨부파일 */}
        <label style={lbl}>
          첨부파일
          {editData && editData.file_ori && (
            <span style={{ color: '#185FA5', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
              현재: {editData.file_ori}
            </span>
          )}
        </label>
        <FileDropZone fileName={file?.name} onFile={setFile} />

        {/* 공개범위 — 계정관리 > 공개범위 설정에서 만든 공개구분 중 선택 */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>공개범위</label>
          <select name="visibility" value={form.visibility} onChange={handleChange} style={inp}>
            <option value="">공개범위를 선택하세요</option>
            {scopeOptions.map(s => <option key={s.id} value={s.name}>{formatScopeLabel(s)}</option>)}
          </select>
        </div>

        <button onClick={handleSubmit} disabled={loading}
          style={{ width: '100%', padding: '12px', background: loading ? '#aaa' : '#185FA5',
            color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '등록 완료')}
        </button>
      </div>
    </div>
  );
}
